import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Truck, Shield, RefreshCw, Headphones } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/ProductCard';
import SEO from '../components/SEO';
import { PLACEHOLDER_IMAGE } from '../lib/placeholder';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await fetch(`${API}/seed`, { method: 'POST' });

        const [featuredRes, bestRes, trendingRes, catRes] = await Promise.all([
          fetch(`${API}/products?featured=true&limit=4`),
          fetch(`${API}/products?bestseller=true&limit=4`),
          fetch(`${API}/products?trending=true&limit=4`),
          fetch(`${API}/categories`)
        ]);

        const [featured, best, trending, cats] = await Promise.all([
          featuredRes.json(),
          bestRes.json(),
          trendingRes.json(),
          catRes.json()
        ]);

        setFeaturedProducts(featured.products || []);
        setBestSellers(best.products || []);
        setTrendingProducts(trending.products || []);
        setCategories(cats || []);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const reviews = [
    {
      id: 1,
      name: 'Priya Sharma',
      rating: 5,
      comment: 'Amazing quality sunglasses! Fast delivery and excellent packaging.',
      product: 'Ray-Ban Aviator'
    },
    {
      id: 2,
      name: 'Rahul Verma',
      rating: 5,
      comment: 'Best place to buy smartphones. Got my iPhone at great price with genuine warranty.',
      product: 'iPhone 15 Pro Max'
    },
    {
      id: 3,
      name: 'Anita Patel',
      rating: 4,
      comment: 'Gift cards delivered instantly. Perfect for last-minute gifts!',
      product: 'Gift Card ₹5000'
    }
  ];

  const categoryImages = {
    'cat_optical': 'https://images.unsplash.com/photo-1574258495973-f010dfbb5371?w=800',
    'cat_mobile': 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=800',
    'cat_giftcard': 'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=800'
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <SEO 
        title="NSO India | Premium E-commerce Destination" 
        description="NSO India is your premium digital marketplace for high-quality optical eyewear, the latest smartphones, and versatile digital gift cards. Shop now for the best deals!"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-surface" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-stagger">
              <span className="inline-block text-sm uppercase tracking-widest text-primary font-body font-semibold mb-4">
                Premium Shopping Experience
              </span>
              <h1 className="font-heading text-3xl sm:text-5xl md:text-7xl tracking-tight leading-none text-secondary mb-6">
                Discover<br />
                <span className="text-primary">Premium</span><br />
                Products
              </h1>
              <p className="text-lg text-muted-foreground font-body mb-8 max-w-md leading-relaxed">
                Shop the finest optical eyewear, latest smartphones, and thoughtful gift cards. Quality you can trust, service you deserve.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  asChild
                  className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-lg font-body btn-press"
                  data-testid="shop-now-btn"
                >
                  <Link to="/category/optical">
                    Shop Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-secondary text-secondary hover:bg-secondary hover:text-white rounded-full px-8 py-6 text-lg font-body btn-press"
                  data-testid="explore-btn"
                >
                  <Link to="/category/mobile">Explore Collection</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-3xl overflow-hidden shadow-hover">
                <img
                  src="https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=800"
                  alt="Premium Sunglasses"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-soft">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center">
                    <Shield className="w-6 h-6 text-success" />
                  </div>
                  <div>
                    <p className="font-body font-semibold text-secondary">100% Authentic</p>
                    <p className="text-sm text-muted-foreground">Genuine Products</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 border-b border-gray-100" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Truck, title: 'Free Shipping', desc: 'On orders above ₹999' },
              { icon: Shield, title: 'Secure Payment', desc: '100% secure checkout' },
              { icon: RefreshCw, title: 'Easy Returns', desc: '7 days return policy' },
              { icon: Headphones, title: '24/7 Support', desc: 'Dedicated support' }
            ].map((feature, idx) => (
              <div key={idx} className="flex items-center gap-4">
                <div className="w-12 h-12 bg-surface rounded-2xl flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-body font-semibold text-secondary text-sm">{feature.title}</p>
                  <p className="text-xs text-muted-foreground">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 md:py-24" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest text-primary font-body font-semibold mb-2 block">
              Shop by Category
            </span>
            <h2 className="font-heading text-2xl md:text-5xl tracking-tight text-secondary">
              Browse Collections
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {categories.map((cat, idx) => (
              <Link
                key={cat.category_id}
                to={`/category/${cat.slug}`}
                className="group relative aspect-[4/5] rounded-3xl overflow-hidden"
                data-testid={`category-link-${cat.slug}`}
              >
                <img
                  src={categoryImages[cat.category_id] || cat.image || PLACEHOLDER_IMAGE}
                  alt={cat.name}
                  onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER_IMAGE) e.currentTarget.src = PLACEHOLDER_IMAGE; }}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="font-heading text-2xl md:text-3xl text-white mb-2">{cat.name}</h3>
                  <p className="text-white/80 font-body text-sm mb-4">{cat.description}</p>
                  <span className="inline-flex items-center text-white font-body text-sm group-hover:gap-3 gap-2 transition-all">
                    Explore <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-16 md:py-24 bg-surface" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <span className="text-sm uppercase tracking-widest text-primary font-body font-semibold mb-2 block">
                  Curated Selection
                </span>
                <h2 className="font-heading text-3xl md:text-5xl tracking-tight text-secondary">
                  Featured Products
                </h2>
              </div>
              <Link
                to="/products?featured=true"
                className="hidden md:flex items-center gap-2 text-primary font-body font-medium hover:gap-3 transition-all"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {featuredProducts.map(product => (
                <ProductCard key={product.product_id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Best Sellers */}
      {bestSellers.length > 0 && (
        <section className="py-16 md:py-24" data-testid="bestsellers-section">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <span className="text-sm uppercase tracking-widest text-primary font-body font-semibold mb-2 block">
                  Most Popular
                </span>
                <h2 className="font-heading text-3xl md:text-5xl tracking-tight text-secondary">
                  Best Sellers
                </h2>
              </div>
              <Link
                to="/products?bestseller=true"
                className="hidden md:flex items-center gap-2 text-primary font-body font-medium hover:gap-3 transition-all"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {bestSellers.map(product => (
                <ProductCard key={product.product_id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Special Offer Banner */}
      <section className="py-16 md:py-24 bg-secondary" data-testid="offer-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-sm uppercase tracking-widest text-primary font-body font-semibold mb-4">
                Limited Time Offer
              </span>
              <h2 className="font-heading text-4xl md:text-6xl tracking-tight text-white mb-6">
                Get 20% Off<br />Your First Order
              </h2>
              <p className="text-lg text-white/70 font-body mb-8 max-w-md">
                Use code <span className="text-primary font-semibold">WELCOME10</span> at checkout. Valid for new customers only.
              </p>
              <Button
                asChild
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-lg font-body btn-press"
                data-testid="claim-offer-btn"
              >
                <Link to="/category/optical">
                  Claim Offer <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
            <div className="relative">
              <div className="aspect-video rounded-3xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1695048133142-1a20484d2569?w=800"
                  alt="Special Offer"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trending Products */}
      {trendingProducts.length > 0 && (
        <section className="py-16 md:py-24" data-testid="trending-section">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <span className="text-sm uppercase tracking-widest text-primary font-body font-semibold mb-2 block">
                  What's Hot
                </span>
                <h2 className="font-heading text-3xl md:text-5xl tracking-tight text-secondary">
                  Trending Now
                </h2>
              </div>
              <Link
                to="/products?trending=true"
                className="hidden md:flex items-center gap-2 text-primary font-body font-medium hover:gap-3 transition-all"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {trendingProducts.map(product => (
                <ProductCard key={product.product_id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Customer Reviews */}
      <section className="py-16 md:py-24 bg-surface" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest text-primary font-body font-semibold mb-2 block">
              Testimonials
            </span>
            <h2 className="font-heading text-3xl md:text-5xl tracking-tight text-secondary">
              What Our Customers Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map(review => (
              <div
                key={review.id}
                className="bg-white p-6 md:p-8 rounded-3xl shadow-soft"
                data-testid={`review-${review.id}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${i < review.rating ? 'fill-warning text-warning' : 'text-gray-200'}`}
                    />
                  ))}
                </div>
                <p className="text-secondary font-body mb-4 leading-relaxed">"{review.comment}"</p>
                <div className="border-t border-gray-100 pt-4">
                  <p className="font-body font-semibold text-secondary">{review.name}</p>
                  <p className="text-sm text-muted-foreground">Purchased: {review.product}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
